import { useMemo, useState } from 'react';
import { useRecords, useStats } from '../hooks/useApi';
import DataTable from '../components/DataTable';
import RecordDetailDrawer from '../components/RecordDetailDrawer';
import SearchFilters, { type SearchFiltersValue } from '../components/SearchFilters';
import type { MSMERecord, PageSize } from '../types';

const DEFAULT_SORT = 'id';
const DEFAULT_ORDER: 'asc' | 'desc' = 'desc';

function fmtDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function valueOf(record: MSMERecord, keys: string[]) {
  for (const key of keys) {
    const v = record[key as keyof MSMERecord];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '—';
}

function recordId(record: MSMERecord) {
  return (
    record.CorporateIdentificationNumber ||
    record.cin ||
    record.id ||
    ''
  );
}

const DEFAULT_PAGE_SIZE: PageSize = 50;

export default function RecordsPage() {
  const [filters, setFilters] = useState<SearchFiltersValue>({
    search: '',
    state: '',
    nic_code: '',
    status: '',
  });
  const [applied, setApplied] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<string>(DEFAULT_SORT);
  const [order, setOrder] = useState<'asc' | 'desc'>(DEFAULT_ORDER);
  const [selectedRecordKey, setSelectedRecordKey] = useState<string | null>(null);

  const { data: stats } = useStats();

  const apiFilters = useMemo(
    () => ({
      search: filters.search,
      state: filters.state,
      nic_code: filters.nic_code,
      status: filters.status,
      limit: pageSize,
      cursor,
      sort,
      order,
    }),
    [filters, cursor, pageSize, sort, order]
  );

  const { data, isLoading, error, isFetching } = useRecords(apiFilters);

  const resetPagination = () => {
    setCursor(undefined);
    setCursorStack([]);
  };

  const handleApply = () => {
    setApplied(true);
    resetPagination();
  };

  const handleSort = (column: string, _nextOrder: 'asc' | 'desc') => {
    if (column !== sort) {
      setSort(column);
      setOrder('asc');
    } else if (order === 'asc') {
      setOrder('desc');
    } else {
      setSort(DEFAULT_SORT);
      setOrder(DEFAULT_ORDER);
    }
    resetPagination();
  };

  const handleResetFilters = (
    next: SearchFiltersValue = {
      search: '',
      state: '',
      nic_code: '',
      status: '',
    }
  ) => {
    setFilters(next);
    setApplied(false);
    resetPagination();
  };

  const handleNext = () => {
    if (data?.nextCursor) {
      setCursorStack((prev) => [...prev, cursor ?? '']);
      setCursor(data.nextCursor);
    }
  };

  const handlePrev = () => {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const prevCursor = stack.pop();
    setCursor(prevCursor || undefined);
    setCursorStack(stack);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size as PageSize);
    resetPagination();
  };

  const handleRowClick = (row: MSMERecord, index: number) => {
    setSelectedRecordKey(
      row.CorporateIdentificationNumber || row.cin || row.id || String(index)
    );
  };

  const columns = useMemo(
    () => [
      {
        key: 'id',
        header: 'ID / CIN',
        sortable: true,
        sortKey: 'id',
        render: (r: MSMERecord) =>
          valueOf(r, ['CorporateIdentificationNumber', 'cin', 'id']),
      },
      {
        key: 'name',
        header: 'Company / Enterprise',
        sortable: true,
        sortKey: 'company_name',
        render: (r: MSMERecord) =>
          valueOf(r, ['CompanyName', 'companyName']),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        sortKey: 'status',
        render: (r: MSMERecord) =>
          valueOf(r, ['CompanyStatus', 'companyStatus', 'status']),
      },
      {
        key: 'state',
        header: 'State',
        sortable: true,
        sortKey: 'state',
        render: (r: MSMERecord) =>
          valueOf(r, [
            'CompanyStateCode',
            'companyStateCode',
            'RegistrationState',
            'registrationState',
            'state',
          ]),
      },
      {
        key: 'nic',
        header: 'NIC Code',
        sortable: true,
        sortKey: 'nic',
        render: (r: MSMERecord) =>
          valueOf(r, [
            'nic_code',
            'NicCode',
            'nicCode',
            'ActivityCode',
            'activityCode',
          ]),
      },
      {
        key: 'activity',
        header: 'Activity / District',
        render: (r: MSMERecord) =>
          valueOf(r, [
            'ActivityDescription',
            'activityDescription',
            'District',
            'district',
            'Address',
          ]),
      },
      {
        key: 'date',
        header: 'Registered',
        sortable: true,
        sortKey: 'registered_date',
        render: (r: MSMERecord) =>
          fmtDate(
            valueOf(r, [
              'DateOfIncorporation',
              'dateOfIncorporation',
              'registrationDate',
              'ApprovedYear',
            ])
          ),
      },
    ],
    []
  );

  const countLabel =
    isLoading || isFetching
      ? 'Loading…'
      : `${(data?.records.length ?? 0).toLocaleString()} records shown${
          typeof data?.total === 'number'
            ? ` of ${data.total.toLocaleString()}`
            : ''
        }`;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-[var(--fg)]">Records</h1>
      <p className="mt-1 text-[var(--muted)]">
        Search and filter across the imported MSME / RoC records.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
          Failed to load records: {error.message}
        </div>
      )}

      <div className="mt-6">
        <SearchFilters
          value={filters}
          onChange={setFilters}
          onApply={handleApply}
          onReset={() => handleResetFilters()}
          loading={isFetching}
          stats={stats}
        />
      </div>

      <div className="mt-4">
        <DataTable
          columns={columns}
          data={data?.records ?? []}
          keyExtractor={(row, index) => recordId(row) || String(index)}
          loading={isLoading}
          isFetching={isFetching}
          emptyMessage={
            applied
              ? 'No records match your filters.'
              : 'Start searching or apply filters to see records.'
          }
          countLabel={countLabel}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pagination={{
            hasPrevious: cursorStack.length > 0,
            hasNext: Boolean(data?.nextCursor),
            onPrevious: handlePrev,
            onNext: handleNext,
            isLoading: isFetching,
          }}
          onRowClick={handleRowClick}
          sort={sort}
          order={order}
          onSort={handleSort}
        />
      </div>

      <RecordDetailDrawer
        recordKey={selectedRecordKey}
        onClose={() => setSelectedRecordKey(null)}
      />
    </div>
  );
}
